"use client"

import React, { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { Loader2, X, Paperclip, ExternalLink, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react"
import DOMPurify from "isomorphic-dompurify"
import { Sheet, SheetPortal } from "@/components/ui/sheet"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { useCodeValues } from "@/hooks/use-code-values"
import { useApi } from "@/lib/api"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useClientRequestsApi, getAttachmentDownloadUrl } from "@/hooks/use-client-requests-api"
import { useParentApp } from "@/contexts/parent-app-context"
import { sendMessageToParent } from "@/lib/app-messaging"
import { buildOnboardingPath } from "@/lib/client-request-action-utils"
import {
  REQUEST_TYPE_TRANSLATION_KEY,
  STATUS_TRANSLATION_KEY,
  STATUS_BADGE_STYLES,
  ATS_STATUS_BADGE_STYLES,
} from "@/lib/status-utils"
import { ATS_STATUS_TRANSLATION_KEY, type AtsPlacementStatus } from "@/types/ats/ats-placements"
import { formatDateOnly, formatDateTime } from "@/utils/format-helpers"
import type {
  ChangeRequestDto,
  ClientRequestDetailDto,
  ClientRequestStatus,
  ClientRequestType,
} from "@/types/client-requests"
import type { VmsEmailPreview } from "@/types/vms/vms-requests"
import { useResizableDrawer, DragHandle } from "@/components/shared/resizable-drawer"

const COMPACT_WIDTH = 430
const FULL_WIDTH = 830
const SNAP_THRESHOLD_RATIO = 0.42
const STORAGE_WIDTH_KEY = "rrp-drawer-width"
const STORAGE_SECTION_KEY = (rid: string) => `rrp-section-collapsed-${rid}`

// ─── Fetch state ──────────────────────────────────────────────────────────────

type ListFetchState = {
  loading: boolean
  error: string | null
  requests: ChangeRequestDto[]
}
type ListFetchAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; requests: ChangeRequestDto[] }
  | { type: "FETCH_ERROR"; error: string }

function listReducer(_: ListFetchState, action: ListFetchAction): ListFetchState {
  switch (action.type) {
    case "FETCH_START":   return { loading: true,  error: null,         requests: [] }
    case "FETCH_SUCCESS": return { loading: false, error: null,         requests: action.requests }
    case "FETCH_ERROR":   return { loading: false, error: action.error, requests: [] }
  }
}

// ─── Detail row helpers ───────────────────────────────────────────────────────

function FieldRow({ label, value, showWhenEmpty = false }: { label: string; value?: string | number | null; showWhenEmpty?: boolean }) {
  const isEmpty = value == null || value === ""
  if (isEmpty && !showWhenEmpty) return null
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "140px 1fr",
      gap: 10,
      padding: "5px 0",
      borderBottom: "1px solid #F1F5F9",
    }}>
      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 14, color: isEmpty ? "#94A3B8" : "#0F172A", wordBreak: "break-word" }}>{isEmpty ? "—" : String(value)}</span>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: "#94A3B8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginTop: 14,
      marginBottom: 6,
    }}>
      {children}
    </div>
  )
}

// ─── Detail content ───────────────────────────────────────────────────────────

function RequestDetailContent({
  requestId,
  fallbackRequestDate,
}: {
  requestId: string
  fallbackRequestDate?: string | null
}) {
  const { t, language } = useTranslation()
  const { fetchWithAuth } = useApi()
  const { getClientRequestDetail } = useClientRequestsApi()
  const codeValues = useCodeValues()

  const handleDownloadAttachment = useCallback(async (fileStorageId: string, fileName: string) => {
    const url = getAttachmentDownloadUrl(requestId, fileStorageId)
    try {
      const response = await fetchWithAuth<Response>(url)
      if (!(response instanceof Response)) {
        throw new Error('Unexpected response type for attachment download')
      }
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Failed to download attachment', err)
    }
  }, [fetchWithAuth, requestId])

  const [data, setData] = useState<ClientRequestDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)

    getClientRequestDetail(requestId)
      .then(result => {
        if (!cancelled) setData(result)
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "error")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [requestId, getClientRequestDetail])

  const resolveRateUnit = (code: string | null | undefined) => {
    if (!code) return undefined
    const text = codeValues.isReady ? codeValues.getCodeValueTextByCode(code, 'workorder.CodeRateUnit') : ''
    return text || code
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0", color: "#94A3B8" }}>
        <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: 14 }}>{t('rrpLoadingDetail')}</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={{ padding: "10px 12px", borderRadius: 6, backgroundColor: "#FEE2E2", border: "1px solid #FECACA", fontSize: 14, color: "#B91C1C" }}>
        {t('rrpDetailLoadError')}
      </div>
    )
  }

  const requestType = data.requestType as ClientRequestType | undefined

  return (
    <div style={{ padding: "12px 14px" }}>
      {/* Request Info */}
      <SectionHeader>{t('requestInfo')}</SectionHeader>
      <FieldRow label={t('requestedBy')} value={data.requestedByName} />
      <FieldRow label={t('rrpRequestDateLabel')} value={fallbackRequestDate ? formatDateTime(fallbackRequestDate, language) : undefined} />
      <FieldRow label={t('rrpActionedBy')} value={data.actionedByUserName} />
      <FieldRow label={t('assignedTo')} value={data.assignedToUserName} />
      <FieldRow label={t('workerFullName')} value={data.workerFullName} />
      <FieldRow label={t('client')} value={data.clientOrganization} />
      <FieldRow label={t('lineOfBusiness')} value={data.lineOfBusiness} />

      {/* Type-specific dates + rates */}
      {(data.startDate || data.endDate || data.billRate != null || data.billRateTypeCode || data.jobTitle || data.hiringManagerName || data.hiringManagerEmail) && (
        <>
          <SectionHeader>{t('requestDetailsHeader')}</SectionHeader>
          <FieldRow label={t('startDate')} value={formatDateOnly(data.startDate, language)} />
          <FieldRow label={t('endDate')} value={formatDateOnly(data.endDate, language)} />
          <FieldRow label={t('rateLabel')} value={data.billRate != null ? String(data.billRate) : null} />
          <FieldRow label={t('rateUnitLabel')} value={resolveRateUnit(data.billRateTypeCode)} />
          <FieldRow label={t('jobTitleLabel')} value={data.jobTitle} />
          <FieldRow label={t('hiringManager')} value={data.hiringManagerName} />
          <FieldRow label={t('hiringManagerEmail')} value={data.hiringManagerEmail} />
        </>
      )}

      {/* Details / notes */}
      {(data.requestDetails || data.terminationDetails) && (
        <>
          <SectionHeader>{requestType === 'Termination' ? t('terminationDetailsSection') : t('notes')}</SectionHeader>
          <div style={{
            padding: "10px 12px",
            fontSize: 14,
            color: "#0F172A",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {data.terminationDetails || data.requestDetails}
          </div>
        </>
      )}

      {/* Comments */}
      <SectionHeader>{t('rrpCommentsHeader')}</SectionHeader>
      {data.comments && data.comments.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.comments.map((c, i) => (
            <div key={i} style={{
              padding: "10px 12px",
              borderRadius: 6,
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>{c.createdByUserName || c.createdByUserEmail || "—"}</span>
                <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>{formatDateTime(c.createdDateTime, language)}</span>
              </div>
              <div style={{ fontSize: 14, color: "#0F172A", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.comment}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>{t('rrpNoComments')}</div>
      )}

      {/* Attachments */}
      {data.attachments && data.attachments.length > 0 && (
        <>
          <SectionHeader>{t('attachmentsLabel')}</SectionHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {data.attachments.map(attachment => (
              <button
                key={attachment.fileStorageId}
                type="button"
                onClick={() => handleDownloadAttachment(attachment.fileStorageId, attachment.name)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 14,
                  color: "#1D4ED8",
                  background: "transparent",
                  border: "none",
                  padding: "4px 0",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Paperclip style={{ width: 14, height: 14, flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Onboarding detail content ────────────────────────────────────────────────

interface OnboardingDetailContentProps {
  requestId: string
  fallbackWorkerName?: string | null
  fallbackWorkerEmail?: string | null
  fallbackClient?: string | null
  fallbackLineOfBusiness?: string | null
  fallbackJobTitle?: string | null
  fallbackStartDate?: string | null
  fallbackEndDate?: string | null
  fallbackBillRate?: number | null
  fallbackRequestDate?: string | null
}

function OnboardingDetailContent({
  requestId,
  fallbackWorkerName,
  fallbackWorkerEmail,
  fallbackClient,
  fallbackLineOfBusiness,
  fallbackJobTitle,
  fallbackStartDate,
  fallbackEndDate,
  fallbackBillRate,
  fallbackRequestDate,
}: OnboardingDetailContentProps) {
  const { t, language } = useTranslation()
  const { syncApi } = useApi()
  const { parentApp } = useParentApp()

  const [data, setData] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)

    syncApi.get<any>(`Request/${requestId}`)
      .then(result => {
        if (!cancelled) setData(result)
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "error")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [requestId, syncApi])

  const handleOpen = useCallback(() => {
    sendMessageToParent({
      action: 'NAVIGATE',
      route: buildOnboardingPath(parentApp, requestId),
    })
  }, [parentApp, requestId])

  const workerName = (data?.worker?.fullName as string | null | undefined) ?? fallbackWorkerName
  const workerEmail = (data?.worker?.email as string | null | undefined) ?? fallbackWorkerEmail
  const client = (data?.clientCompany?.text as string | null | undefined) ?? fallbackClient
  const jobTitle = (data?.jobTitle?.text as string | null | undefined) ?? fallbackJobTitle
  const lineOfBusiness = (data?.lineOfBusiness?.text as string | null | undefined) ?? fallbackLineOfBusiness
  const startDate = (data?.jobStartDate as string | null | undefined) ?? fallbackStartDate
  const endDate = (data?.jobEndDate as string | null | undefined) ?? fallbackEndDate
  const anticipatedStartDate = data?.anticipatedStartDate as string | null | undefined
  const assignedTo = data?.assignedToProfile?.fullName as string | null | undefined
  const hiringManager = data?.hiringManagerProfile?.fullName as string | null | undefined
  const hiringManagerEmail = data?.hiringManagerProfile?.email as string | null | undefined
  const billRate = (data?.billRate?.rate as number | null | undefined) ?? fallbackBillRate
  const billRateUnit = data?.billRate?.unit?.text as string | null | undefined
  const payRate = data?.payRate?.rate as number | null | undefined
  const payRateUnit = data?.payRate?.unit?.text as string | null | undefined
  const comments = data?.comments as string | null | undefined

  return (
    <div style={{ padding: "12px 14px" }}>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 6,
          border: "1px solid #CBD5E1",
          backgroundColor: "#FFFFFF",
          color: "#1D4ED8",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          marginBottom: 8,
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#F1F5F9")}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#FFFFFF")}
      >
        <ExternalLink style={{ width: 14, height: 14 }} />
        {t('rrpOpenOnboardingRequest')}
      </button>

      {loading && !data && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", color: "#94A3B8" }}>
          <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: 14 }}>{t('rrpLoadingDetail')}</span>
        </div>
      )}

      <SectionHeader>{t('requestInfo')}</SectionHeader>
      <FieldRow label={t('rrpRequestDateLabel')} value={fallbackRequestDate ? formatDateTime(fallbackRequestDate, language) : undefined} />
      <FieldRow label={t('workerFullName')} value={workerName} />
      <FieldRow label={t('client')} value={client} />
      <FieldRow label={t('assignedTo')} value={assignedTo} />
      <FieldRow label={t('lineOfBusiness')} value={lineOfBusiness} />

      <SectionHeader>{t('requestDetailsHeader')}</SectionHeader>
      <FieldRow label={t('jobTitleLabel')} value={jobTitle} />
      <FieldRow label={t('rrpAnticipatedStartDate')} value={formatDateOnly(anticipatedStartDate, language)} />
      <FieldRow label={t('startDate')} value={formatDateOnly(startDate, language)} />
      <FieldRow label={t('endDate')} value={formatDateOnly(endDate, language)} />
      <FieldRow label={`${t('billRate')} (${t('rateLabel')})`} value={billRate != null ? String(billRate) : null} />
      <FieldRow label={`${t('billRate')} (${t('rateUnitLabel')})`} value={billRateUnit} />
      <FieldRow label={`${t('payRate')} (${t('rateLabel')})`} value={payRate != null ? String(payRate) : null} />
      <FieldRow label={`${t('payRate')} (${t('rateUnitLabel')})`} value={payRateUnit} />
      <FieldRow label={t('hiringManager')} value={hiringManager} />
      <FieldRow label={t('hiringManagerEmail')} value={hiringManagerEmail} />

      {comments && (
        <>
          <SectionHeader>{t('notes')}</SectionHeader>
          <div style={{
            padding: "10px 12px",
            fontSize: 14,
            color: "#0F172A",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {comments}
          </div>
        </>
      )}

      {error && !data && (
        <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 6, backgroundColor: "#FEE2E2", border: "1px solid #FECACA", fontSize: 14, color: "#B91C1C" }}>
          {t('rrpDetailLoadError')}
        </div>
      )}
    </div>
  )
}

// ─── ATS placement-request detail (no fetch — data sourced from the index row) ───

// TODO: Add a "View full ATS placement request" button that opens the underlying
// AtsPlacementRequest Cosmos doc. Needs a new GET endpoint
// (e.g. /api/workflow/v2/ats-placement-requests/{id}) that fetches the doc by id and
// reverse-maps it into the client shape we expose today.
// VMS rows arrive through the shared gw-client-request-index alongside
// ClientRequest rows but get their own detail card. There's no separate
// /api/vms-request/{id} read endpoint exposed for the panel today — we
// rely on the same summary fields the row already carries from the
// search projection. Once a dedicated VmsRequest detail endpoint exists
// (mirroring ClientRequest's), this can fetch + show richer content.
function VmsRequestDetailContent({ request }: { request: ChangeRequestDto }) {
  const { t, language } = useTranslation()
  const codeValues = useCodeValues()

  const resolveCode = (value: string | undefined | null, group: string) => {
    if (!value) return undefined
    return codeValues.isReady ? (codeValues.getCodeValueTextByCode(value, group) || value) : value
  }

  return (
    <div style={{ padding: "12px 14px" }}>
      {/* Pre-verification onboarding request link — same CTA the ATS card uses. Shown when this
          VMS request has a linked onboarding Request (PreVerificationRequestId is projected for
          VMS rows too, via ClientRequestIndex.ToSearchDto). Keeps the panel consistent across
          ATS / VMS / Request — they all surface their onboarding request the same way. */}
      {request.preVerificationRequestId && (
        <OpenOnboardingRequestButton requestId={request.preVerificationRequestId} />
      )}
      <SectionHeader>{t('requestInfo')}</SectionHeader>
      <FieldRow label={t('rrpRequestDateLabel')} value={request.dateRequested ? formatDateTime(request.dateRequested, language) : undefined} />
      <FieldRow label={t('workerFullName')} value={request.workerName} showWhenEmpty />
      <FieldRow label={t('client')} value={request.clientOrg} />
      <FieldRow label="Worker organization" value={request.workerOrg} />
      <FieldRow label={t('assignedTo')} value={request.assignedToUserFullName ?? request.requestedByName} />
      <FieldRow label={t('lineOfBusiness')} value={resolveCode(request.lineOfBusiness, 'org.CodeLineOfBusiness')} />
      <FieldRow label="Profile type" value={resolveCode(request.profileType, 'usr.CodeProfileType')} />
      <FieldRow label={t('startDate')} value={formatDateOnly(request.startDate, language)} showWhenEmpty />
      <FieldRow label={t('endDate')} value={formatDateOnly(request.endDate, language)} showWhenEmpty />
      <FieldRow label={`${t('billRate')} (${t('rateLabel')})`} value={request.billRate != null ? String(request.billRate) : null} showWhenEmpty />

      {/* Original email — VMS-only, lazy-loaded on first expand. Skip the whole
          section if the row doesn't carry a linkedEmailId (e.g. completed VMS
          requests that arrived via a non-email source path or older rows that
          predate the field). */}
      {request.linkedEmailId && (
        <VmsOriginalEmailSection emailId={request.linkedEmailId} />
      )}
    </div>
  )
}

/**
 * Collapsible "Original email" section for VMS-source rows in the related-
 * requests panel. Stays collapsed on mount; on the first expand it fires
 * <c>fetchVmsEmailPreview</c> against the linked email id and caches the
 * payload so subsequent expand/collapse cycles don't re-hit the API.
 *
 * In-flight dedupe via inflightRef defends against React 18 Strict Mode's
 * double-mount-in-dev and rapid toggle clicks. HTML bodies are sanitized
 * with DOMPurify (same defaults as VmsEmailPreviewPanel — strip scripts /
 * iframes / forms / styles); plain-text bodies render in a monospace block
 * with whitespace preserved so the sender's line breaks survive.
 *
 * Errors fall back to a small inline warning instead of disappearing the
 * section — operators should know the fetch failed and can retry by
 * collapsing + re-expanding.
 */
function VmsOriginalEmailSection({ emailId }: { emailId: string }) {
  const { t } = useTranslation()
  const { fetchVmsEmailPreview } = useWorkflowApi()

  const [expanded, setExpanded] = useState(false)
  const [preview, setPreview] = useState<VmsEmailPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchedRef = useRef(false)
  const inflightRef = useRef<string | null>(null)

  const toggle = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  // Fire the fetch on the FIRST expand only. Putting this in an effect (vs
  // doing the fetch inside toggle) keeps the spinner reactive to retries —
  // a future "reload" affordance can just flip fetchedRef back to false.
  //
  // No cleanup function on purpose. React 18 Strict Mode dev-mounts the
  // component twice; a cancelled-flag pattern would block the first run's
  // promise from ever committing state (the second run sees inflightRef
  // already set and returns early, so nothing else updates loading/preview
  // either, and the UI gets stuck on the spinner forever). Let the original
  // fetch complete and update state directly — if the component truly
  // unmounts before the fetch resolves React simply discards the state
  // updates, which is the same outcome the cancelled-flag was trying to
  // achieve.
  useEffect(() => {
    if (!expanded) return
    if (fetchedRef.current) return
    if (inflightRef.current === emailId) return

    inflightRef.current = emailId
    setLoading(true)
    setError(null)
    fetchVmsEmailPreview(emailId)
      .then(result => {
        fetchedRef.current = true
        setPreview(result)
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load email')
      })
      .finally(() => {
        setLoading(false)
        if (inflightRef.current === emailId) inflightRef.current = null
      })
  }, [expanded, emailId, fetchVmsEmailPreview])

  return (
    <div style={{ marginTop: 18 }}>
      {/* Clickable header — chevron flips, label same visual weight as
          SectionHeader so it reads as part of the section list. The whole
          row is the hit target. */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 700,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 6,
        }}
      >
        {expanded
          ? <ChevronDown size={12} />
          : <ChevronRight size={12} />}
        {t('originalEmail') || 'Original email'}
      </button>

      {expanded && (
        <div style={{
          background: '#F8FAFC',
          border: '1px solid #E2E8F0',
          borderRadius: 6,
          padding: 10,
          fontSize: 12,
        }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748B' }}>
              <Loader2 size={14} className="animate-spin" />
              <span>{t('loading') || 'Loading…'}</span>
            </div>
          )}

          {error && !loading && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#B91C1C' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{error}</span>
            </div>
          )}

          {preview && !loading && !error && (
            <VmsOriginalEmailBody preview={preview} />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Read-only render of a VmsEmailPreview payload — header metadata followed
 * by the decrypted body. Kept separate from the section wrapper so the
 * loading / error / loaded states stay easy to read in the parent.
 */
function VmsOriginalEmailBody({ preview }: { preview: VmsEmailPreview }) {
  const { language } = useTranslation()
  const html = preview.bodyHtml?.trim()
  const text = preview.bodyPlain?.trim()
  // Prefer HTML body when both are present — that's how the sender intended
  // the message to render. Fall back to plain text for emails that didn't
  // ship an HTML part. Sanitize HTML with DOMPurify; plain text renders in
  // a <pre> so whitespace + line breaks survive.
  const useHtml = !!html
  const sanitized = useHtml
    ? DOMPurify.sanitize(html!, { FORBID_TAGS: ['script', 'style', 'iframe', 'form'] })
    : null

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: 4, marginBottom: 8 }}>
        {preview.subject && (
          <>
            <span style={{ color: '#64748B' }}>Subject</span>
            <span style={{ color: '#0F172A', fontWeight: 500, wordBreak: 'break-word' }}>{preview.subject}</span>
          </>
        )}
        {preview.from && (
          <>
            <span style={{ color: '#64748B' }}>From</span>
            <span style={{ color: '#0F172A', wordBreak: 'break-all' }}>{preview.from}</span>
          </>
        )}
        {preview.to && (
          <>
            <span style={{ color: '#64748B' }}>To</span>
            <span style={{ color: '#0F172A', wordBreak: 'break-all' }}>{preview.to}</span>
          </>
        )}
        {preview.originalSentAt && (
          <>
            <span style={{ color: '#64748B' }}>Received</span>
            <span style={{ color: '#0F172A' }}>{formatDateTime(preview.originalSentAt, language)}</span>
          </>
        )}
      </div>

      <div style={{
        borderTop: '1px solid #E2E8F0',
        paddingTop: 8,
        maxHeight: 360,
        overflowY: 'auto',
      }}>
        {useHtml ? (
          <div
            // Sanitized above — DOMPurify strips scripts/iframes/forms/styles.
            // The container scopes the email's inline styles to this panel
            // section so anything that survives sanitization (e.g. inline
            // colour, font-size) can't leak out and reskin the host panel.
            style={{ color: '#0F172A', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: sanitized! }}
          />
        ) : text ? (
          <pre style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'inherit',
            margin: 0,
            color: '#0F172A',
          }}>
            {text}
          </pre>
        ) : (
          <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>
            (Empty body)
          </span>
        )}
      </div>
    </div>
  )
}

// Shared "Open onboarding request" CTA. Navigates the parent app to the OB Portal
// onboarding request by its portal id (PreVerificationRequestId). Used by the ATS
// detail card (always — every ATS placement has a pre-verification onboarding
// request) and mirrors the button OnboardingDetailContent renders for Onboarding
// ClientRequests, so both source families expose the same link at the top.
function OpenOnboardingRequestButton({ requestId }: { requestId: string }) {
  const { t } = useTranslation()
  const { parentApp } = useParentApp()
  const handleOpen = useCallback(() => {
    sendMessageToParent({
      action: 'NAVIGATE',
      route: buildOnboardingPath(parentApp, requestId),
    })
  }, [parentApp, requestId])

  return (
    <button
      type="button"
      onClick={handleOpen}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        borderRadius: 6,
        border: "1px solid #CBD5E1",
        backgroundColor: "#FFFFFF",
        color: "#1D4ED8",
        fontSize: 14,
        fontWeight: 500,
        cursor: "pointer",
        marginBottom: 8,
      }}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#F1F5F9")}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#FFFFFF")}
    >
      <ExternalLink style={{ width: 14, height: 14 }} />
      {t('rrpOpenOnboardingRequest')}
    </button>
  )
}

function AtsPlacementRequestDetailContent({ request }: { request: ChangeRequestDto }) {
  const { t, language } = useTranslation()
  const codeValues = useCodeValues()

  // Match the ATS-tab grid: Code value table -> friendly text, fall back to the raw code.
  // Worker email and bill rate are stripped server-side for Client users — no extra
  // gating needed here.
  const resolveCode = (value: string | undefined | null, group: string) => {
    if (!value) return undefined
    return codeValues.isReady ? (codeValues.getCodeValueTextByCode(value, group) || value) : value
  }

  return (
    <div style={{ padding: "12px 14px" }}>
      {/* Onboarding request link — shown only when this ATS placement has a
          pre-verification onboarding request (PreVerificationRequestId isn't always
          populated). Same CTA the Onboarding ClientRequest card uses. Replaces the raw
          "Pre-verification request ID" row that used to sit at the bottom of this card. */}
      {request.preVerificationRequestId && (
        <OpenOnboardingRequestButton requestId={request.preVerificationRequestId} />
      )}
      <SectionHeader>{t('requestInfo')}</SectionHeader>
      <FieldRow label={t('rrpRequestDateLabel')} value={request.dateRequested ? formatDateTime(request.dateRequested, language) : undefined} />
      <FieldRow label="Request Completed" value={request.dateCompleted ? formatDateTime(request.dateCompleted, language) : undefined} />
      <FieldRow label={t('workerFullName')} value={request.workerName} showWhenEmpty />
      {/* Worker email and bill rate are scrubbed server-side for Client users — keep the
          rows visible (showWhenEmpty) so the layout stays stable for everyone. */}
      <FieldRow label="Worker email" value={request.workerEmail} showWhenEmpty />
      <FieldRow label={t('client')} value={request.clientOrg} />
      <FieldRow label="Worker organization" value={request.workerOrg} />
      <FieldRow label={t('assignedTo')} value={request.requestedByName} />
      <FieldRow label={t('lineOfBusiness')} value={resolveCode(request.lineOfBusiness, 'org.CodeLineOfBusiness')} />
      <FieldRow label="Profile type" value={resolveCode(request.profileType, 'usr.CodeProfileType')} />
      <FieldRow label={t('jobTitleLabel')} value={request.jobTitle} />
      <FieldRow label={t('startDate')} value={formatDateOnly(request.startDate, language)} showWhenEmpty />
      <FieldRow label={t('endDate')} value={formatDateOnly(request.endDate, language)} showWhenEmpty />
      <FieldRow label={`${t('billRate')} (${t('rateLabel')})`} value={request.billRate != null ? String(request.billRate) : null} showWhenEmpty />
      <FieldRow label="ATS placement ID" value={request.atsPlacementId} />
      <FieldRow label="ATS source" value={resolveCode(request.atsSource, 'etl.CodeAtsSource')} />
    </div>
  )
}

// ─── Section (one request) ────────────────────────────────────────────────────

function RelatedRequestSection({
  request,
}: {
  request: ChangeRequestDto
}) {
  const { t } = useTranslation()
  const requestId = request.requestId || request.changeRequestId
  const storageKey = requestId ? STORAGE_SECTION_KEY(requestId) : null

  const [collapsed, setCollapsed] = useState(() => {
    if (!storageKey) return true
    try {
      const stored = localStorage.getItem(storageKey)
      return stored !== null ? stored === "true" : true
    } catch {
      return true
    }
  })

  const toggleCollapsed = useCallback(() => {
    setCollapsed(v => {
      const next = !v
      if (storageKey) {
        try { localStorage.setItem(storageKey, String(next)) } catch {}
      }
      return next
    })
  }, [storageKey])

  const isAts = request.source === 'AtsPlacementRequest'
  const isVms = request.source === 'VmsRequest'
  // source === 'Request' is a gw-sync Request (Onboarding / Extension / Termination /
  // Redeployment raised in the OB portal). It lives in the gw-sync service, not the
  // workflow-service client-request store — so its detail must be fetched from Request/{id},
  // NOT /api/client-request/{id}. (Distinct from source === 'ClientRequest'.)
  const isRequest = request.source === 'Request'
  const status = request.status as ClientRequestStatus | undefined
  const requestType = request.requestType as ClientRequestType | undefined
  const atsStatus = isAts ? (request.atsStatus as AtsPlacementStatus | undefined) : undefined

  // A ClientRequest of type Onboarding is the pre-onboarding (client-submitted) request that
  // precedes the OB Portal onboarding Request (source = Request). Both carry requestType
  // 'Onboarding', so without this the panel shows two identical "Onboarding" rows — label the
  // ClientRequest one "Pre-Onboarding" to tell them apart.
  const isPreOnboarding = request.source === 'ClientRequest' && requestType === 'Onboarding'

  // ATS placement rows aren't ClientRequestType — show a friendly label rather than the raw enum.
  // VMS rows use the same ClientRequestType set (Onboarding/Extension/…) but we prefix the label
  // so operators immediately see this came from a VMS email rather than a portal-submitted form.
  const typeLabel = isAts
    ? "ATS Placement Request"
    : isVms
      ? requestType && REQUEST_TYPE_TRANSLATION_KEY[requestType]
        ? `VMS · ${t(REQUEST_TYPE_TRANSLATION_KEY[requestType])}`
        : `VMS Request${requestType ? ` · ${requestType}` : ""}`
      : isPreOnboarding
        ? t("requestTypePreOnboarding")
        : requestType && REQUEST_TYPE_TRANSLATION_KEY[requestType]
          ? t(REQUEST_TYPE_TRANSLATION_KEY[requestType])
          : requestType ?? "—"

  // ATS rows pull from the ATS status palette/translation map (matches the ATS tab grid).
  // VMS rows reuse the ClientRequest palette — they share the same status set
  // (RequestInitiated / RequestAccepted / OnHold / Canceled / …).
  const statusStyle = isAts
    ? (atsStatus ? ATS_STATUS_BADGE_STYLES[atsStatus] : undefined)
    : (status ? STATUS_BADGE_STYLES[status] : undefined)
  const statusLabel = isAts
    ? (atsStatus && ATS_STATUS_TRANSLATION_KEY[atsStatus]
        ? t(ATS_STATUS_TRANSLATION_KEY[atsStatus])
        : (request.atsStatus ?? "—"))
    : (status && STATUS_TRANSLATION_KEY[status]
        ? t(STATUS_TRANSLATION_KEY[status])
        : (request.status ?? "—"))

  const accentColor = statusStyle?.text ?? "#005CB9"

  return (
    <div style={{
      marginBottom: 8,
      borderRadius: 8,
      border: "1px solid #E2E8F0",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={toggleCollapsed}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCollapsed() } }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 12px",
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: "#F8FAFC",
          borderLeft: `3px solid ${accentColor}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          <span style={{
            fontSize: 12,
            color: "#64748B",
            display: "inline-block",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            lineHeight: 1,
          }}>
            ▼
          </span>
          <span style={{
            fontWeight: 700,
            fontSize: 15,
            color: "#0F172A",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {typeLabel}
          </span>
        </div>
        {statusStyle ? (
          <span style={{
            display: "inline-block",
            padding: "3px 12px",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 500,
            background: statusStyle.bg,
            color: statusStyle.text,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}>
            {statusLabel}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: "#64748B", flexShrink: 0 }}>{statusLabel}</span>
        )}
      </div>

      {!collapsed && (
        isAts ? (
          <AtsPlacementRequestDetailContent request={request} />
        ) : isVms ? (
          // VMS rows render their own summary card from the search-row fields —
          // no extra fetch yet, since we don't expose a /api/vms-request/{id}
          // read endpoint for the panel. Mirrors the ATS branch's "what we
          // already have" approach.
          <VmsRequestDetailContent request={request} />
        ) : isRequest ? (
          // source === 'Request' (gw-sync portal Request: Extension / Termination / etc.).
          // Show the unified list-row data immediately (as fallbacks), AND fetch the gw-sync
          // Request/{id} detail to enrich it (pay rate, hiring manager, comments, anticipated
          // start, …) when the call succeeds — degrading gracefully to the list summary if it
          // doesn't. Keyed by the row's own requestId (the gw-sync doc id). OnboardingDetailContent
          // is the gw-sync Request fetcher (the generic Request shape covers Extension/Termination/etc.).
          <OnboardingDetailContent
            requestId={requestId ?? ''}
            fallbackWorkerName={request.workerName}
            fallbackWorkerEmail={request.workerEmail}
            fallbackClient={request.clientOrg}
            fallbackLineOfBusiness={request.lineOfBusiness}
            fallbackJobTitle={request.jobTitle}
            fallbackStartDate={request.startDate}
            fallbackEndDate={request.endDate}
            fallbackBillRate={request.billRate}
            fallbackRequestDate={request.dateRequested}
          />
        ) : requestId ? (
          requestType === 'Onboarding' ? (
            <OnboardingDetailContent
              // Onboarding detail + Open nav address the gw-sync Request by its
              // portal id (PreVerificationRequestId), not the ClientRequest doc id
              // (`requestId` is now uniformly the doc id after the RequestId swap).
              requestId={request.preVerificationRequestId ?? ''}
              fallbackWorkerName={request.workerName}
              fallbackClient={request.clientOrg}
              fallbackLineOfBusiness={request.lineOfBusiness}
              fallbackStartDate={request.startDate}
              fallbackEndDate={request.endDate}
              fallbackRequestDate={request.dateRequested}
            />
          ) : (
            <RequestDetailContent
              requestId={requestId}
              fallbackRequestDate={request.dateRequested}
            />
          )
        ) : null
      )}
    </div>
  )
}

// Drag-resize hook + DragHandle moved to @/components/shared/resizable-drawer
// so VMS email preview and any future right-side panel get the same behaviour
// without copy-paste drift. Local constants (COMPACT_WIDTH/FULL_WIDTH/etc.)
// are still used here as the params we pass into the shared hook below.

// ─── Main panel ───────────────────────────────────────────────────────────────

interface RelatedRequestsPanelProps {
  isOpen: boolean
  onClose: () => void
  fboAssignmentId: number | undefined
  workerName?: string
}

export function RelatedRequestsPanel({ isOpen, onClose, fboAssignmentId, workerName }: RelatedRequestsPanelProps) {
  const { t } = useTranslation()
  const { fetchRelatedClientRequests } = useWorkflowApi()
  const [{ loading, error, requests }, dispatch] = useReducer(listReducer, {
    loading: false,
    error: null,
    requests: [],
  })
  const { currentWidth, dragging, onHandleMouseDown, toggleMode, isFull } = useResizableDrawer({
    compactWidth: COMPACT_WIDTH,
    fullWidth: FULL_WIDTH,
    snapThresholdRatio: SNAP_THRESHOLD_RATIO,
    storageKey: STORAGE_WIDTH_KEY,
  })

  useEffect(() => {
    if (!isOpen || !fboAssignmentId) return
    let cancelled = false

    dispatch({ type: "FETCH_START" })
    fetchRelatedClientRequests(fboAssignmentId)
      .then(result => {
        if (cancelled) return
        dispatch({ type: "FETCH_SUCCESS", requests: result })
      })
      .catch(e => {
        if (cancelled) return
        dispatch({ type: "FETCH_ERROR", error: e?.message ?? "error" })
      })

    return () => { cancelled = true }
  }, [isOpen, fboAssignmentId, fetchRelatedClientRequests])

  return (
    <Sheet open={isOpen} onOpenChange={open => { if (!open) onClose() }}>
      <SheetPortal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <SheetPrimitive.Content
          style={{
            width: currentWidth,
            maxWidth: '100vw',
            transition: dragging ? "none" : "width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "fixed",
            insetBlock: 0,
            right: 0,
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.10)",
            overflow: "visible",
          }}
        >
          <DragHandle
            onMouseDown={onHandleMouseDown}
            onToggle={toggleMode}
            isFull={isFull}
            expandLabel={t('rrpExpandPanel')}
            collapseLabel={t('rrpCollapsePanel')}
          />

          <div style={{
            flexShrink: 0,
            padding: "14px 16px 12px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
          }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
              {t('rrpTitle')}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>
                {workerName ?? "—"}
              </span>
              <SheetPrimitive.Close
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  color: "#64748B",
                  flexShrink: 0,
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#F1F5F9")}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <X style={{ width: 16, height: 16 }} />
                <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>{t('close')}</span>
              </SheetPrimitive.Close>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0, position: "relative" }}>
            {loading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 16px", gap: 8, color: "#94A3B8" }}>
                <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />
                <span style={{ fontSize: 14 }}>{t('rrpLoadingRequests')}</span>
              </div>
            )}

            {!loading && error && (
              <div style={{ margin: 16, padding: "12px 16px", borderRadius: 8, backgroundColor: "#FEE2E2", border: "1px solid #FECACA", fontSize: 14, color: "#B91C1C" }}>
                {t('rrpLoadError')}
              </div>
            )}

            {!loading && !error && requests.length === 0 && (
              <p style={{ textAlign: "center", padding: "48px 16px", fontSize: 14, color: "#94A3B8", fontStyle: "italic" }}>
                {t('rrpNoRequests')}
              </p>
            )}

            {!loading && !error && requests.length > 0 && (
              <div style={{ padding: "8px 12px 12px" }}>
                {requests.map(request => (
                  <RelatedRequestSection
                    key={request.requestId ?? request.changeRequestId}
                    request={request}
                  />
                ))}
              </div>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  )
}
